import { useEffect, useRef, useState } from "react";
import {
  Alert, AlertDescription, AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  AlertIcon, Badge, Box, Button, Flex, FormControl, FormLabel, Input, SimpleGrid, Spinner, Tbody, Td, Text, Th, Thead, Tr, useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { crm } from "../../../theme";
import { downloadVoidXml, FiscalEnvironment, FiscalNumberVoid, getNumberVoids, voidNumbers } from "../../../services/fiscalService";
import { TableCard, tdProps, thProps } from "../../process/components/tableStyles";

const MAX_RANGE = 1000;

const statusInfo: Record<FiscalNumberVoid["status"], { label: string; color: string }> = {
  authorized: { label: "Inutilizada", color: "green" },
  rejected: { label: "Recusada", color: "red" },
  unknown: { label: "Sem resposta", color: "orange" },
};

/** Inteiro positivo digitado (vazio/inválido = NaN). */
const toInt = (v: string) => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : NaN);

/** Inutilização de numeração do emitente: faixa + justificativa e a lista do que já foi pedido à SEFAZ. */
const FiscalNumberVoids = ({ issuerId, environment }: { issuerId: string; environment: FiscalEnvironment }) => {
  const toast = useToast();
  const [list, setList] = useState<FiscalNumberVoid[] | null>(null);
  const [series, setSeries] = useState("1");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const load = () => getNumberVoids(issuerId).then(setList).catch(() => { setList([]); toast({ status: "error", title: "Não foi possível carregar as inutilizações" }); });
  useEffect(() => { load(); }, [issuerId]);

  const s = toInt(series);
  const a = toInt(start);
  const b = toInt(end === "" ? start : end);
  const cleanReason = reason.replace(/\s+/g, " ").trim();
  const count = b - a + 1;
  const formErrors = [
    !(s >= 0 && s <= 999) && "Série de 0 a 999.",
    !(a >= 1) && "Informe o número inicial.",
    a >= 1 && !(b >= a) && "O número final não pode ser menor que o inicial.",
    count > MAX_RANGE && `No máximo ${MAX_RANGE} números por vez.`,
    cleanReason.length > 0 && (cleanReason.length < 15 || cleanReason.length > 255) && "Justificativa com 15 a 255 caracteres.",
  ].filter(Boolean) as string[];
  const valid = formErrors.length === 0 && cleanReason.length >= 15;

  const submit = async () => {
    setConfirm(false);
    setBusy(true);
    const result = await voidNumbers(issuerId, { series: s, startNumber: a, endNumber: b, justification: cleanReason });
    setBusy(false);
    setErrors(result.success ? [] : result.errors);
    if (result.void) load();
    if (result.success) {
      toast({ status: "success", title: `Numeração ${a}${b !== a ? `–${b}` : ""} inutilizada` });
      setStart(""); setEnd(""); setReason("");
    }
  };

  const download = async (id: string) => {
    try { await downloadVoidXml(id); }
    catch { toast({ status: "error", title: "Não foi possível baixar o XML" }); }
  };

  return (
    <Box mt="10px" p="12px" bg={crm.subtle} borderRadius="10px">
      <Text fontSize="12px" color={crm.textMuted} mb="10px">
        Para números que não vão ser usados (nota recusada abandonada, número pulado). Nº já usado por nota autorizada, cancelada ou denegada
        não pode, nem nº acima do maior já usado (a Focus ainda vai dar). A inutilização não tem volta. Ambiente atual: <b>{environment === "producao" ? "produção" : "homologação"}</b>.
      </Text>
      {errors.length > 0 && (
        <Alert status="error" borderRadius="10px" mb="10px" alignItems="flex-start">
          <AlertIcon />
          <AlertDescription fontSize="13px" whiteSpace="pre-line">{errors.join("\n")}</AlertDescription>
        </Alert>
      )}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing="10px">
        <FormControl>
          <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Série</FormLabel>
          <Input size="sm" bg="white" inputMode="numeric" value={series} onChange={(e) => setSeries(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Nº inicial</FormLabel>
          <Input size="sm" bg="white" inputMode="numeric" value={start} onChange={(e) => setStart(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Nº final (opcional)</FormLabel>
          <Input size="sm" bg="white" inputMode="numeric" value={end} onChange={(e) => setEnd(e.target.value)} />
        </FormControl>
        <Flex align="flex-end">
          <Button size="sm" colorScheme="red" w="100%" isDisabled={!valid} isLoading={busy} onClick={() => setConfirm(true)}>Inutilizar</Button>
        </Flex>
      </SimpleGrid>
      <FormControl mt="10px">
        <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Justificativa (vai para a SEFAZ, 15 a 255 caracteres)</FormLabel>
        <Input size="sm" bg="white" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
      </FormControl>
      {(start !== "" || reason !== "") && formErrors.length > 0 && <Text fontSize="12px" color="red.500" mt="6px">{formErrors.join(" ")}</Text>}

      <Box mt="14px">
        {!list ? (
          <Spinner size="sm" />
        ) : list.length === 0 ? (
          <Text fontSize="13px" color={crm.textMuted}>Nenhuma inutilização.</Text>
        ) : (
          <TableCard>
            <Thead>
              <Tr>
                <Th {...thProps}>Série</Th>
                <Th {...thProps}>Números</Th>
                <Th {...thProps}>Situação</Th>
                <Th {...thProps}>Justificativa / retorno</Th>
                <Th {...thProps}>Quando / quem</Th>
                <Th {...thProps}></Th>
              </Tr>
            </Thead>
            <Tbody>
              {list.map((v) => (
                <Tr key={v.id}>
                  <Td {...tdProps}>{v.series}</Td>
                  <Td {...tdProps} whiteSpace="nowrap" fontWeight="600">{v.startNumber}{v.endNumber !== v.startNumber ? `–${v.endNumber}` : ""}</Td>
                  <Td {...tdProps}>
                    <Badge colorScheme={statusInfo[v.status]?.color}>{statusInfo[v.status]?.label ?? v.status}</Badge>
                    {v.environment === "homologacao" && <Badge ml="4px" colorScheme="purple" fontSize="10px">Homologação</Badge>}
                  </Td>
                  <Td {...tdProps} minW="260px">
                    <Text>{v.justification}</Text>
                    {(v.errors || v.sefazMessage) && <Text fontSize="12px" color={crm.textMuted} whiteSpace="pre-line">{v.errors ?? `SEFAZ ${v.sefazStatus}: ${v.sefazMessage}`}</Text>}
                  </Td>
                  <Td {...tdProps} whiteSpace="nowrap" color={crm.textMuted}>{DateTime.fromISO(v.createdDate).toFormat("dd/LL/yyyy HH:mm")} · {v.userName ?? "—"}</Td>
                  <Td {...tdProps}>{v.hasXml && <Button size="xs" variant="outline" onClick={() => download(v.id)}>XML</Button>}</Td>
                </Tr>
              ))}
            </Tbody>
          </TableCard>
        )}
      </Box>

      <AlertDialog isOpen={confirm} leastDestructiveRef={cancelRef} onClose={() => setConfirm(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="16px">Inutilizar numeração?</AlertDialogHeader>
            <AlertDialogBody fontSize="14px">
              Série {s}, {count === 1 ? `número ${a}` : `números ${a} a ${b} (${count} números)`}, em {environment === "producao" ? "PRODUÇÃO" : "homologação"}.
              Depois de inutilizados, esses números não podem mais ser usados.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirm(false)}>Voltar</Button>
              <Button colorScheme="red" ml={3} onClick={submit}>Inutilizar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default FiscalNumberVoids;
